import React, { useEffect, useState } from 'react';
import financialService from '../../services/financialService';

interface ReconciliationReport {
    _id: string;
    date: string;
    providers: Array<{
        provider: string;
        internalBalance: number;
        providerBalance: number;
        discrepancy: number;
    }>;
    totalDiscrepancy: number;
    created_at: string;
}

const AdminFinancialDashboard = () => {
    const [verifications, setVerifications] = useState<any[]>([]);
    const [reconciliation, setReconciliation] = useState<any>(null);
    const [reconciliationHistory, setReconciliationHistory] = useState<ReconciliationReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingReconcile, setLoadingReconcile] = useState(false);
    const [activeTab, setActiveTab] = useState<'kyc' | 'reconciliation'>('kyc');
    const [selectedVerifications, setSelectedVerifications] = useState<Set<string>>(new Set());

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const pending = await financialService.getPendingVerifications();
            setVerifications(pending);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleReview = async (userId: string, action: 'approve' | 'reject', level?: string) => {
        const reason = prompt('Enter reason/note:');
        try {
            await financialService.adminReviewVerification({ userId, action, level, reason: reason || '' });
            alert('Review processed and user notified via email.');
            loadData();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleBulkApprove = async (level: 'tier1' | 'tier2') => {
        if (selectedVerifications.size === 0) {
            alert('Please select verifications to approve');
            return;
        }

        if (!confirm(`Approve ${selectedVerifications.size} verification(s) as ${level}?`)) {
            return;
        }

        const reason = prompt('Enter bulk approval reason:') || 'Bulk approved';

        try {
            setLoading(true);
            for (const userId of Array.from(selectedVerifications)) {
                await financialService.adminReviewVerification({
                    userId,
                    action: 'approve',
                    level,
                    reason
                });
            }
            alert(`Successfully approved ${selectedVerifications.size} verification(s)`);
            setSelectedVerifications(new Set());
            loadData();
        } catch (e: any) {
            alert('Bulk approval failed: ' + e.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelection = (userId: string) => {
        const newSet = new Set(selectedVerifications);
        if (newSet.has(userId)) {
            newSet.delete(userId);
        } else {
            newSet.add(userId);
        }
        setSelectedVerifications(newSet);
    };

    const handleReconcile = async () => {
        try {
            setLoadingReconcile(true);
            const res = await financialService.reconcile();
            setReconciliation(res);
            alert('Reconciliation Complete. Check report below.');
        } catch (e: any) {
            alert(e.message);
        } finally {
            setLoadingReconcile(false);
        }
    };

    return (
        <div className="min-h-screen bg-premium-wood-dark text-white p-8 ml-64">
            <h1 className="text-3xl font-serif text-premium-gold mb-8">Financial Admin Dashboard</h1>

            {/* Tabs */}
            <div className="flex gap-4 mb-8 border-b border-white/10">
                <button
                    onClick={() => setActiveTab('kyc')}
                    className={`px-6 py-3 font-medium transition ${activeTab === 'kyc' ? 'text-premium-gold border-b-2 border-premium-gold' : 'text-white/50 hover:text-white/80'}`}
                >
                    KYC Verifications
                </button>
                <button
                    onClick={() => setActiveTab('reconciliation')}
                    className={`px-6 py-3 font-medium transition ${activeTab === 'reconciliation' ? 'text-premium-gold border-b-2 border-premium-gold' : 'text-white/50 hover:text-white/80'}`}
                >
                    Reconciliation
                </button>
            </div>

            {/* KYC Tab */}
            {activeTab === 'kyc' && (
                <div className="space-y-6">
                    {/* Bulk Actions */}
                    {selectedVerifications.size > 0 && (
                        <div className="p-4 bg-premium-gold/10 rounded-xl border border-premium-gold/30 flex items-center justify-between">
                            <span className="text-premium-gold font-medium">
                                {selectedVerifications.size} verification(s) selected
                            </span>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => handleBulkApprove('tier1')}
                                    className="px-4 py-2 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition"
                                >
                                    Bulk Approve Tier1
                                </button>
                                <button
                                    onClick={() => handleBulkApprove('tier2')}
                                    className="px-4 py-2 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition"
                                >
                                    Bulk Approve Tier2
                                </button>
                                <button
                                    onClick={() => setSelectedVerifications(new Set())}
                                    className="px-4 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition"
                                >
                                    Clear Selection
                                </button>
                            </div>
                        </div>
                    )}

                    {/* KYC List */}
                    <div className="p-6 bg-white/5 rounded-xl border border-white/10">
                        <h2 className="text-xl font-bold mb-4">Pending Verifications</h2>
                        {loading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin w-8 h-8 border-4 border-premium-gold border-t-transparent rounded-full mx-auto mb-2"></div>
                                <p className="text-white/50">Loading...</p>
                            </div>
                        ) : verifications.length === 0 ? (
                            <p className="text-white/50">No pending requests.</p>
                        ) : (
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-white/10 text-white/50 text-xs uppercase">
                                        <th className="p-3">
                                            <input
                                                type="checkbox"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedVerifications(new Set(verifications.map(v => v.user_id)));
                                                    } else {
                                                        setSelectedVerifications(new Set());
                                                    }
                                                }}
                                                checked={selectedVerifications.size === verifications.length && verifications.length > 0}
                                            />
                                        </th>
                                        <th className="p-3">User ID</th>
                                        <th className="p-3">Email</th>
                                        <th className="p-3">Documents</th>
                                        <th className="p-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {verifications.map(v => (
                                        <tr key={v._id} className="border-b border-white/5 hover:bg-white/5">
                                            <td className="p-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedVerifications.has(v.user_id)}
                                                    onChange={() => toggleSelection(v.user_id)}
                                                />
                                            </td>
                                            <td className="p-3 font-mono text-sm text-white/70">{v.user_id}</td>
                                            <td className="p-3 font-mono text-sm text-white/70">{v.user_email || 'N/A'}</td>
                                            <td className="p-3">
                                                {v.documents && v.documents.map((d: any, i: number) => (
                                                    <div key={i} className="text-xs mb-1">
                                                        <span className="opacity-50">{d.type}: </span>
                                                        <a href={d.url} target="_blank" rel="noreferrer" className="text-premium-gold hover:underline">View</a>
                                                    </div>
                                                ))}
                                            </td>
                                            <td className="p-3 flex gap-2">
                                                <button onClick={() => handleReview(v.user_id, 'approve', 'tier1')} className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30">Approve T1</button>
                                                <button onClick={() => handleReview(v.user_id, 'approve', 'tier2')} className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30">Approve T2</button>
                                                <button onClick={() => handleReview(v.user_id, 'reject')} className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30">Reject</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Reconciliation Tab */}
            {activeTab === 'reconciliation' && (
                <div className="space-y-6">
                    {/* Reconciliation Trigger */}
                    <div className="p-6 bg-white/5 rounded-xl border border-white/10">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Daily Reconciliation</h2>
                            <button
                                onClick={handleReconcile}
                                disabled={loadingReconcile}
                                className="bg-premium-gold text-black px-4 py-2 rounded font-medium hover:bg-premium-gold/90 transition disabled:opacity-50"
                            >
                                {loadingReconcile ? 'Running...' : 'Run Audit'}
                            </button>
                        </div>
                        {reconciliation && (
                            <div className="mt-4">
                                <p className="text-sm text-white/60 mb-2">Report ID: {reconciliation._id}</p>
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div className="p-4 bg-black/30 rounded-lg">
                                        <div className="text-xs text-white/50 mb-1">Date</div>
                                        <div className="text-lg font-bold">{reconciliation.date}</div>
                                    </div>
                                    <div className="p-4 bg-black/30 rounded-lg">
                                        <div className="text-xs text-white/50 mb-1">Providers Checked</div>
                                        <div className="text-lg font-bold">{reconciliation.providers?.length || 0}</div>
                                    </div>
                                    <div className="p-4 bg-black/30 rounded-lg">
                                        <div className="text-xs text-white/50 mb-1">Total Discrepancy</div>
                                        <div className={`text-lg font-bold ${reconciliation.totalDiscrepancy === 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            ${reconciliation.totalDiscrepancy?.toFixed(2) || '0.00'}
                                        </div>
                                    </div>
                                </div>
                                <pre className="bg-black/50 p-4 rounded text-xs overflow-auto max-h-60 text-green-400">
                                    {JSON.stringify(reconciliation, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminFinancialDashboard;
